import { useState } from 'react';
import { Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useIsAdmin } from '../lib/auth/useIsAdmin';

/**
 * Admin-only day navigation toolbar rendered above the shared
 * `<DailyLayout>` on both `<DailyPage>` (`/`) and `<DayDetailPage>`
 * (`/daily/:date`).
 *
 * Renders nothing for non-admin visitors — the toolbar's own
 * visibility is the primary access gate; the underlying data
 * available to admins is still `filterConsumerVisibleContent`-scoped
 * (admins see approved-and-system-auto-approved content on any date)
 * until the follow-up landing the "unapproved content on day view"
 * admin surface (public-website Requirement 12, next round).
 *
 * The date picker uses the browser's native `<input type="date">`
 * so no additional MUI X dependency is required. Any date is
 * selectable, including future ones — admins are expected to preview
 * a day's scheduled auto-publish content ahead of its release
 * window.
 *
 * Function-length budget: every export below stays under 20 lines per
 * Requirement 3.7.
 */
export interface AdminDayNavProps {
  /** Current date being viewed, in `YYYY-MM-DD` format. */
  readonly currentDate: string;
}

/** `YYYY-MM-DD` for today in the visitor's local timezone. */
function todayLocal(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function AdminDayNav({ currentDate }: AdminDayNavProps): JSX.Element | null {
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const [draft, setDraft] = useState<string>(currentDate);
  if (!isAdmin) return null;
  const today = todayLocal();
  const isFuture = currentDate > today;
  const isPast = currentDate < today && currentDate.length > 0;
  // 2026-09-12 fix: the previous "change event with length === 10"
  // heuristic silently dropped user input when browser autofill or
  // programmatic date changes fired an onChange with an unexpected
  // value. Replace with an explicit "Go" button + Enter-key submit
  // so the operator always knows their click will land.
  const submit = (): void => {
    if (draft.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(draft) && draft !== currentDate) {
      navigate(`/daily/${draft}`);
    }
  };
  return (
    <Box
      sx={{
        p: 1.5,
        border: '1px dashed',
        borderColor: 'primary.main',
        borderRadius: 1,
        bgcolor: 'primary.50',
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { xs: 'flex-start', sm: 'center' } }}
      >
        <Typography variant="overline" color="text.secondary">
          Admin view
        </Typography>
        <TextField
          type="date"
          size="small"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          slotProps={{ htmlInput: { 'aria-label': 'View a specific date' } }}
        />
        <Button
          size="small"
          variant="contained"
          onClick={submit}
          disabled={draft.length !== 10 || draft === currentDate}
        >
          Go
        </Button>
        {isFuture && <Chip label="future date" size="small" color="warning" />}
        {isPast && <Chip label="historical" size="small" color="info" variant="outlined" />}
        {currentDate === today && <Chip label="today" size="small" color="success" />}
        <Box sx={{ flexGrow: 1 }} />
        <Typography
          variant="caption"
          component="a"
          onClick={() => navigate(`/daily/${today}`)}
          sx={{ cursor: 'pointer', textDecoration: 'underline', color: 'primary.main' }}
        >
          Jump to today
        </Typography>
      </Stack>
    </Box>
  );
}
